import type { JSX } from 'solid-js'
import { createEffect, createMemo, For, Show } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import { parseMonthDate } from '../charts/config/chart-formatters'

export interface ComparisonTableContext<TItem> {
  readonly data: () => TItem[]
  readonly getMonth: (item: TItem) => string
  readonly isLastColumn: (monthStr: string) => boolean
  readonly isCurrentMonth: (monthStr: string) => boolean
  readonly formatMonthHeader: (monthStr: string) => string
  readonly getHeaderCellClass: (monthStr: string) => string
  readonly getValueCellClass: (monthStr: string) => string
  readonly getRowLabelCellClass: (variant?: 'default' | 'strong') => string
}

export interface ComparisonTableProps<TItem> {
  readonly data: TItem[]
  readonly currentMonth?: string
  readonly highlightMonth?: string
  readonly title: JSX.Element
  readonly getMonth: (item: TItem) => string
  readonly maxMonths?: number
  readonly noDataFallback?: JSX.Element
  readonly titleCellClass?: string
  readonly rowLabelCellClass?: string
  readonly rowLabelCellClassStrong?: string
  readonly formatHeader?: (dateStr: string) => string
  readonly containerClass?: string
  readonly tableClass?: string
  readonly theadClass?: string
  readonly headerRowClass?: string
  readonly tbodyClass?: string
  readonly getHeaderCellClass?: (args: { monthStr: string; isCurrentMonth: boolean; isLastColumn: boolean }) => string
  readonly getValueCellClass?: (args: { monthStr: string; isCurrentMonth: boolean; isLastColumn: boolean }) => string
  readonly children: (context: ComparisonTableContext<TItem>) => JSX.Element
}

const DEFAULT_MAX_MONTHS = 6

const ComparisonTable = <TItem,>(props: ComparisonTableProps<TItem>) => {
  const getCurrentCalendarMonthString = (): string => {
    const now = new Date()
    const year = now.getFullYear()
    const paddedMonth = String(now.getMonth() + 1).padStart(2, '0')
    return `${year}-${paddedMonth}-01`
  }

  const filteredData = createMemo(() => {
    const maxMonths = props.maxMonths ?? DEFAULT_MAX_MONTHS

    if (!props.currentMonth || !props.data || props.data.length === 0) {
      return props.data?.slice(-maxMonths) ?? []
    }

    const current = parseMonthDate(props.currentMonth)
    if (!current) return props.data.slice(-maxMonths)

    const currentMonthIndex = props.data.findIndex((item) => {
      const itemDate = parseMonthDate(props.getMonth(item))
      return (
        itemDate !== null &&
        itemDate.getFullYear() === current.getFullYear() &&
        itemDate.getMonth() === current.getMonth()
      )
    })

    if (currentMonthIndex === -1) {
      return props.data.slice(-maxMonths)
    }

    const startIndex = Math.max(0, currentMonthIndex - (maxMonths - 1))
    const endIndex = currentMonthIndex + 1
    return props.data.slice(startIndex, endIndex)
  })

  const displayFormatHeader = (monthStr: string): string => {
    if (props.formatHeader) return props.formatHeader(monthStr)
    const date = parseMonthDate(monthStr)
    if (!date) return ''
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()
  }

  const isCurrentMonth = (monthStr: string): boolean => {
    const highlightMonth = props.highlightMonth ?? getCurrentCalendarMonthString()
    return monthStr === highlightMonth
  }

  const isLastColumn = (monthStr: string): boolean => {
    const data = filteredData()
    if (data.length === 0) return false
    return props.getMonth(data[data.length - 1]) === monthStr
  }

  const defaultHeaderCellClass = (monthStr: string): string => {
    const base = 'px-6 py-3 text-right text-sm font-medium whitespace-nowrap text-gray-900'
    if (isCurrentMonth(monthStr)) {
      return `${base} border-b-2 border-[#3947F2] bg-[#EDF3FF]`
    }
    return base
  }

  const defaultValueCellClass = (monthStr: string): string => {
    const base = 'px-6 py-3 whitespace-nowrap text-sm text-right'
    if (isCurrentMonth(monthStr)) {
      return `${base} text-gray-900 bg-gray-50`
    }
    return `${base} text-gray-900`
  }

  const getHeaderCellClass = (monthStr: string): string => {
    if (props.getHeaderCellClass) {
      return props.getHeaderCellClass({
        monthStr,
        isCurrentMonth: isCurrentMonth(monthStr),
        isLastColumn: isLastColumn(monthStr)
      })
    }
    return defaultHeaderCellClass(monthStr)
  }

  const getValueCellClass = (monthStr: string): string => {
    if (props.getValueCellClass) {
      return props.getValueCellClass({
        monthStr,
        isCurrentMonth: isCurrentMonth(monthStr),
        isLastColumn: isLastColumn(monthStr)
      })
    }
    return defaultValueCellClass(monthStr)
  }

  const noDataFallback = () =>
    props.noDataFallback ?? (
      <div class="text-center text-gray-500 py-12">
        <Trans key="ls_generic_no_data" />
      </div>
    )

  const titleCellClass = () =>
    props.titleCellClass ?? 'px-4 py-[14px] text-left text-sm font-semibold text-[#1e40af] bg-gray-50'

  const getRowLabelCellClass = (variant: 'default' | 'strong' = 'default'): string => {
    if (variant === 'strong') {
      return props.rowLabelCellClassStrong ?? 'text-gray-900 font-semibold'
    }
    return props.rowLabelCellClass ?? 'text-gray-700'
  }

  const containerClass = () =>
    `rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden${
      props.containerClass ? ` ${props.containerClass}` : ''
    }`

  let scrollRef: HTMLDivElement | undefined

  createEffect(() => {
    filteredData()
    const el = scrollRef
    if (el) {
      requestAnimationFrame(() => {
        el.scrollLeft = el.scrollWidth
      })
    }
  })

  return (
    <Show when={filteredData().length > 0} fallback={noDataFallback()}>
      <div class={`${containerClass()} relative`}>
        <div ref={scrollRef} class="overflow-x-auto">
          <table class={props.tableClass ?? 'siroc-table siroc-table--sticky table-fixed min-w-full bg-white'}>
            <thead class={props.theadClass ?? 'bg-gray-50'}>
              <tr class={props.headerRowClass ?? 'text-left text-sm font-semibold'}>
                <th
                  class={`${titleCellClass()} sticky left-0 z-20 border-r border-gray-300`}
                  style={{ 'box-shadow': '2px 0 4px -2px rgba(0,0,0,0.1)' }}
                >
                  {props.title}
                </th>
                <For each={filteredData()}>
                  {(item) => {
                    const monthStr = props.getMonth(item)
                    return <th class={getHeaderCellClass(monthStr)}>{displayFormatHeader(monthStr)}</th>
                  }}
                </For>
              </tr>
            </thead>
            <tbody class={props.tbodyClass ?? 'text-gray-900'}>
              {props.children({
                data: filteredData,
                getMonth: props.getMonth,
                isLastColumn,
                isCurrentMonth,
                formatMonthHeader: displayFormatHeader,
                getHeaderCellClass,
                getValueCellClass,
                getRowLabelCellClass
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Show>
  )
}

export default ComparisonTable
